const isStripeHostedCheckout = () => (
  typeof window !== 'undefined'
  && /checkout\.stripe\.com$/i.test(window.location.hostname)
);

export const restoreHiddenIonPages = () => {
  if (typeof document === 'undefined') return;

  document.getElementById('loading_startup')?.style.setProperty('display', 'none');

  const pages = Array.from(document.querySelectorAll('.ion-page'));
  if (!pages.length) return;

  const visible = pages.filter((page) => (
    !page.classList.contains('ion-page-hidden')
    && !page.classList.contains('ion-page-invisible')
  ));
  if (visible.length) return;

  const page = pages[pages.length - 1];
  page.classList.remove('ion-page-hidden', 'ion-page-invisible');
  page.removeAttribute('aria-hidden');
  page.style.removeProperty('display');
  page.style.removeProperty('opacity');
  page.style.removeProperty('visibility');
  page.style.removeProperty('pointer-events');
};

export const syncIosPwaViewportMetrics = () => {
  if (typeof window === 'undefined') return;

  const vv = window.visualViewport;
  const height = Math.round(vv?.height ?? window.innerHeight);
  const offsetTop = Math.round(vv?.offsetTop ?? 0);

  document.documentElement.style.setProperty('--ollaa-viewport-height', `${height}px`);
  document.documentElement.style.setProperty('--ollaa-viewport-offset-top', `${offsetTop}px`);
};

const dispatchLayoutRefresh = () => {
  window.dispatchEvent(new Event('resize'));
  if (window.visualViewport) {
    window.visualViewport.dispatchEvent(new Event('resize'));
  }
};

/**
 * iOS PWA can return from Stripe's in-app browser with a stale layout viewport:
 * fixed tab bar at visual bottom while ion-tabs/content end higher → white gap above navbar.
 * Closing and reopening the app recalculates; this does the same in-place.
 */
export const repairIosPwaLayoutAfterExternalBrowser = () => {
  if (typeof window === 'undefined') return;

  restoreHiddenIonPages();

  document.documentElement.style.removeProperty('height');
  document.body.style.removeProperty('height');

  syncIosPwaViewportMetrics();
  window.scrollTo(0, 0);
  dispatchLayoutRefresh();

  requestAnimationFrame(() => {
    syncIosPwaViewportMetrics();
    dispatchLayoutRefresh();
  });

  window.setTimeout(() => {
    syncIosPwaViewportMetrics();
    dispatchLayoutRefresh();
  }, 120);

  window.setTimeout(() => {
    syncIosPwaViewportMetrics();
    dispatchLayoutRefresh();
  }, 320);
};

let installed = false;

/**
 * @param {object} [options]
 * @param {string} [options.stripeCheckoutSessionKey] sessionStorage key set before Stripe redirect
 * @param {() => object|null} [options.readLocationCheckoutQuery] returns checkout query from location
 * @param {() => void} [options.onBfCacheWithoutCheckout] reopen membership modal instead of reload
 * @param {() => boolean} [options.onStripeMembershipReturn] membership checkout URL landed in PWA
 */
export const installIosPwaLayoutRepair = (options = {}) => {
  if (typeof window === 'undefined' || installed) return;
  installed = true;

  const stripeCheckoutSessionKey = options.stripeCheckoutSessionKey;
  const readLocationCheckoutQuery = options.readLocationCheckoutQuery || (() => null);

  const recover = (event) => {
    if (document.visibilityState === 'hidden') return;
    if (isStripeHostedCheckout()) return;

    let membershipReturnHandled = false;
    if (typeof options.onStripeMembershipReturn === 'function') {
      membershipReturnHandled = options.onStripeMembershipReturn();
    }

    if (stripeCheckoutSessionKey) {
      const fromBfCache = Boolean(event?.persisted);
      const checkoutStarted = sessionStorage.getItem(stripeCheckoutSessionKey) === '1';
      const hasCheckoutQuery = Boolean(readLocationCheckoutQuery()?.checkout);

      if (fromBfCache && checkoutStarted && !hasCheckoutQuery) {
        if (typeof options.onBfCacheWithoutCheckout === 'function') {
          options.onBfCacheWithoutCheckout();
          membershipReturnHandled = true;
        } else {
          sessionStorage.removeItem(stripeCheckoutSessionKey);
          window.location.reload();
          return;
        }
      }
    }

    if (membershipReturnHandled) {
      restoreHiddenIonPages();
      syncIosPwaViewportMetrics();
      window.scrollTo(0, 0);
      return;
    }

    repairIosPwaLayoutAfterExternalBrowser();
  };

  syncIosPwaViewportMetrics();

  window.addEventListener('pageshow', recover);
  document.addEventListener('visibilitychange', () => recover());
  window.visualViewport?.addEventListener('resize', syncIosPwaViewportMetrics);
  window.visualViewport?.addEventListener('scroll', syncIosPwaViewportMetrics);
  window.addEventListener('resize', syncIosPwaViewportMetrics);
};
